import React from 'react'
import { History } from 'history'
import { inject, observer, Observer } from 'mobx-react'
import { useDropzone } from 'react-dropzone'
import { AnimatePresence, motion } from 'framer-motion'
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { AddPhotosStore, FileData, ADD_PHOTOS_STORE } from '../../stores/AddPhotosStore'
import { TwButton } from '../../components/Button/TwButton'
import { PageContent } from '../../components/Common/PageContent'
import { SectionHeader } from '../../components/SectionHeader/SectionHeader'
import { DropdownMenu, DropdownMenuItem } from '../../components/DropdownMenu'
import {
  CloudArrowUpIcon as CloudUploadIcon,
  PauseIcon,
  PlayIcon as ResumeIcon,
  PlusIcon as AddMoreIcon,
  CheckIcon,
  ExclamationCircleIcon as ErrorIcon,
  ExclamationTriangleIcon as WarningIcon,
  XMarkIcon as CloseIcon,
  TrashIcon as ClearAllIcon,
} from '@heroicons/react/24/outline'
import { AlbumAutocomplete } from '../../components/AlbumAutocomplete'
import { WorkspaceAutocomplete } from '../../components/WorkspaceAutocomplete'
import { IWorkspace } from '../../services/api'
import { ACCEPTED_FILE_TYPES } from '../../constants/acceptedFileTypes'
import AlbumModel from '../../models/album'
import { Skeleton } from '../../components/Skeleton'

interface AddPhotosProps {
  history: History
  [ADD_PHOTOS_STORE]: AddPhotosStore
}

const AddPhotosInner: React.FC<AddPhotosProps> = observer((props) => {
  const { t } = useTranslation()
  const store = props[ADD_PHOTOS_STORE]
  const { files, filesCount, isUploading, uploadedCount, allFilesUploaded, waitForCancel } = store

  const progress = filesCount === 0 ? 0 : Math.round((uploadedCount / filesCount) * 100)

  const onDrop = React.useCallback(
    (acceptedFiles: File[]) => {
      if (!acceptedFiles.length) return
      const filesData = acceptedFiles.map((file) => {
        const key = `${file.name}${file.lastModified}${file.size}`
        return new FileData(key, file)
      })
      if (store.filesCount === 0) {
        store.setFiles(filesData)
      } else {
        store.queueFiles(filesData)
      }
    },
    [store]
  )

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: ACCEPTED_FILE_TYPES,
    noClick: true,
    noKeyboard: true,
  })

  return (
    <div {...getRootProps()} className="relative min-h-0">
      <input {...getInputProps()} />

      <PageContent maxWidth="max-w-5xl">
        
        <SectionHeader
          title="Prześlij zdjęcia"
          description="Wybierz zdjęcia z urządzenia i prześlij je do swojej galerii."
          actions={
            <>
              {/* Desktop buttons - hidden on small mobile */}
              {!isUploading && filesCount > 0 && (
                <>
                  <TwButton
                    variant="outline"
                    icon={<ClearAllIcon />}
                    onClick={store.removeFiles}
                    className="hidden sm:inline-flex"
                  >
                    Wyczyść
                  </TwButton>
                  <TwButton
                    variant="outline"
                    icon={<AddMoreIcon />}
                    onClick={open}
                    className="hidden sm:inline-flex"
                  >
                    Dodaj więcej
                  </TwButton>
                </>
              )}
              
              {/* Mobile dropdown menu - only on very small screens */}
              {!isUploading && filesCount > 0 && (
                <DropdownMenu
                  className="sm:hidden"
                  items={[
                    {
                      label: 'Dodaj więcej',
                      icon: <AddMoreIcon className="h-5 w-5" />,
                      onClick: open,
                    },
                    {
                      label: 'Wyczyść wszystko',
                      icon: <ClearAllIcon className="h-5 w-5" />,
                      onClick: store.removeFiles,
                      variant: 'danger',
                    },
                  ]}
                />
              )}
              
              {/* Pause button */}
              {isUploading && (
                <TwButton
                  variant="solid"
                  color="danger"
                  icon={<PauseIcon />}
                  disabled={waitForCancel}
                  onClick={store.stopUpload}
                >
                  {waitForCancel ? 'Zatrzymywanie...' : 'Zatrzymaj'}
                </TwButton>
              )}
              
              {/* Resume button */}
              {((!isUploading && uploadedCount > 0) || waitForCancel) && !allFilesUploaded && (
                <TwButton
                  variant="solid"
                  color="primary"
                  icon={<ResumeIcon />}
                  onClick={store.startUpload}
                >
                  Wznów
                </TwButton>
              )}
              
              {/* Start upload button */}
              {!isUploading && filesCount > 0 && uploadedCount === 0 && (
                <TwButton
                  variant="solid"
                  color="primary"
                  icon={<CloudUploadIcon />}
                  onClick={store.startUpload}
                >
                  Wyślij {filesCount}
                </TwButton>
              )}
            </>
          }
        >
          {/* Progress bar - only show when there are files */}
          {filesCount > 0 && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-gray-600">
                  {isUploading ? 'Przesyłanie...' : uploadedCount > 0 ? 'Przesłano' : 'Gotowe do przesłania'}
                </span>
                <span className="text-gray-900 font-medium">{uploadedCount} z {filesCount}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                <div 
                  className="bg-indigo-600 h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}
        </SectionHeader>
        
        {/* Thumbnail Grid or Empty State */}
        <Observer>
          {() => {
            if (files.length === 0) {
              return (
                <div 
                  onClick={open}
                  className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${
                    isDragActive
                      ? 'border-indigo-400 bg-indigo-50/50'
                      : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                    isDragActive ? 'bg-indigo-100' : 'bg-gray-100'
                  }`}>
                    {isDragActive ? (
                      <ArrowUpTrayIcon className="h-8 w-8 text-indigo-500" />
                    ) : (
                      <CloudUploadIcon className="h-8 w-8 text-gray-400" />
                    )}
                  </div>
                  <p className="text-lg font-medium text-gray-900 mb-1">
                    {isDragActive ? t('dropZone.dropHere') : 'Przeciągnij zdjęcia tutaj'}
                  </p>
                  {!isDragActive && (
                    <p className="text-sm text-gray-500 mb-4">lub kliknij, aby wybrać pliki</p>
                  )}
                  <p className="text-xs text-gray-400">{t('dropZone.supportedFormats')}</p>
                </div>
              )
            }
            
            const ws = store.workspacesStore

            return (
              <div className="pb-8">
                {/* Bulk selectors */}
                <BulkSelectors
                  workspaces={ws.workspaces}
                  activeWorkspace={ws.activeWorkspace}
                  files={files}
                  disabled={isUploading}
                />

                <ul className="divide-y divide-gray-200">
                {files.map((fileData) => {
                  return (
                    <UploadFileRow
                      key={fileData.key}
                      fileData={fileData}
                      isUploading={isUploading}
                      workspaces={ws.workspaces}
                      onRemove={() => store.removeFile(fileData)}
                    />
                  )
                })}
                </ul>
              </div>
            )
          }}
        </Observer>
      </PageContent>

      <AnimatePresence>
        {isDragActive && filesCount > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="flex flex-col items-center gap-4 rounded-2xl border-2 border-dashed border-indigo-400 bg-white/90 px-16 py-12 shadow-lg"
            >
              <div className="rounded-full bg-indigo-50 p-4">
                <ArrowUpTrayIcon className="h-10 w-10 text-indigo-500" />
              </div>
              <p className="text-lg font-medium text-gray-800">
                {t('dropZone.dropHere')}
              </p>
              <p className="text-sm text-gray-400">
                {t('dropZone.supportedFormats')}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
})

export const AddPhotos = inject(ADD_PHOTOS_STORE)(AddPhotosInner)

// Helper to format file size
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// List row component
interface UploadFileRowProps {
  fileData: FileData
  isUploading: boolean
  workspaces: IWorkspace[]
  onRemove: () => void
}

const UploadFileRow: React.FC<UploadFileRowProps> = observer(({ fileData, isUploading, workspaces, onRemove }) => {
  const { hasError, upload, uploaded, miniatureUrl, miniatureLoaded, errorMessage, isDuplicate, file, album } = fileData
  // Use only the workspace captured at add time, no live fallback.
  const selectedWorkspace = workspaces.find((w) => w.id === fileData.workspaceId)
  const workspaceId = fileData.workspaceId
  const progress = upload?.progress ?? 0
  const isCurrentlyUploading = isUploading && upload && !uploaded && !hasError
  const isLocked = isUploading || uploaded

  return (
    <li className="py-3 px-1">
      <div className="flex gap-4">
      {/* Thumbnail - fixed square */}
      <div className="h-20 w-20 shrink-0 overflow-hidden rounded-lg bg-gray-100">
        {miniatureUrl ? (
          <img
            src={miniatureUrl}
            alt=""
            className={`h-full w-full object-cover ${!miniatureLoaded ? 'animate-pulse' : ''}`}
          />
        ) : (
          <Skeleton className="h-full w-full" />
        )}
      </div>

      {/* Right side: info + selectors */}
      <div className="min-w-0 flex-1 flex flex-col justify-center gap-1.5">
        {/* File info row */}
        <div className="flex items-center gap-4">
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-gray-900 leading-5">{file.name}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs text-gray-500">{formatFileSize(file.size)}</span>

              {/* Status indicators */}
              {isCurrentlyUploading && (
                <span className="text-xs text-indigo-600 font-medium">{Math.round(progress)}%</span>
              )}
              {uploaded && !hasError && (
                <span className="inline-flex items-center gap-1 text-xs text-green-700">
                  <CheckIcon className="h-3.5 w-3.5" />
                  Przesłano
                </span>
              )}
              {hasError && isDuplicate && (
                <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                  <WarningIcon className="h-3.5 w-3.5" />
                  Już przesłano
                </span>
              )}
              {hasError && !isDuplicate && (
                <span className="inline-flex items-center gap-1 text-xs text-red-600">
                  <ErrorIcon className="h-3.5 w-3.5" />
                  {errorMessage || 'Błąd'}
                </span>
              )}
            </div>

            {/* Progress bar - always reserved space */}
            <div className="mt-1.5 h-1 w-full rounded-full bg-gray-200 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-150 ${isCurrentlyUploading ? 'bg-indigo-600' : 'bg-transparent'}`}
                style={{ width: isCurrentlyUploading ? `${progress}%` : '0%' }}
              />
            </div>
          </div>

          {/* Remove button */}
          {(!isUploading && !uploaded) && (
            <button
              type="button"
              onClick={onRemove}
              className="shrink-0 rounded-md p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <CloseIcon className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Workspace + Album selectors */}
        <div className="flex items-center gap-2">
          <div className="w-40 sm:w-44">
            <WorkspaceAutocomplete
              workspaces={workspaces}
              value={selectedWorkspace}
              onChange={(ws) => {
                if (ws) fileData.setWorkspaceId(ws.id)
              }}
              disabled={isLocked}
              placeholder="Workspace"
            />
          </div>
          <div className="w-48 sm:w-56">
            <AlbumAutocomplete
              workspaceId={workspaceId}
              value={album}
              onChange={(a) => fileData.setAlbum(a)}
              disabled={isLocked}
              placeholder="Album"
            />
          </div>
        </div>
      </div>
      </div>
    </li>
  )
})

// Bulk selectors to apply workspace/album to all files at once
interface BulkSelectorsProps {
  workspaces: IWorkspace[]
  activeWorkspace?: IWorkspace
  files: FileData[]
  disabled: boolean
}

const BulkSelectors: React.FC<BulkSelectorsProps> = observer(({ workspaces, activeWorkspace, files, disabled }) => {
  const [bulkWorkspace, setBulkWorkspace] = React.useState<IWorkspace | undefined>(undefined)
  const [bulkAlbum, setBulkAlbum] = React.useState<AlbumModel | undefined>(undefined)

  const effectiveWorkspace = bulkWorkspace || activeWorkspace
  const workspaceId = effectiveWorkspace?.id || ''

  // Detect mixed selections across non-uploaded files
  const pendingFiles = files.filter((f) => !f.uploaded)
  const workspaceIds = new Set(pendingFiles.map((f) => f.workspaceId))
  const albumIds = new Set(pendingFiles.map((f) => f.album?.id || ''))
  const isMixedWorkspace = workspaceIds.size > 1
  const isMixedAlbum = albumIds.size > 1

  // Determine displayed values — show undefined when mixed so placeholder is visible
  const displayedWorkspace = isMixedWorkspace ? undefined : effectiveWorkspace
  const displayedAlbum = isMixedAlbum ? undefined : bulkAlbum

  const handleWorkspaceChange = (ws: IWorkspace | undefined) => {
    setBulkWorkspace(ws)
    if (ws) {
      files.forEach((f) => {
        if (!f.uploaded) f.setWorkspaceId(ws.id)
      })
      // Clear album when workspace changes since albums are workspace-scoped
      setBulkAlbum(undefined)
    }
  }

  const handleAlbumChange = (album: AlbumModel | undefined) => {
    setBulkAlbum(album)
    files.forEach((f) => {
      if (!f.uploaded) f.setAlbum(album)
    })
  }

  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
      <span className="text-xs font-medium text-gray-500 mr-1">Zastosuj do wszystkich:</span>
      <div className="w-44 sm:w-48">
        <WorkspaceAutocomplete
          workspaces={workspaces}
          value={displayedWorkspace}
          onChange={handleWorkspaceChange}
          disabled={disabled}
          placeholder={isMixedWorkspace ? 'Różne workspaces' : 'Workspace'}
        />
      </div>
      <div className="w-52 sm:w-60">
        <AlbumAutocomplete
          workspaceId={workspaceId}
          value={displayedAlbum}
          onChange={handleAlbumChange}
          disabled={disabled}
          placeholder={isMixedAlbum ? 'Różne albumy' : 'Album'}
        />
      </div>
    </div>
  )
})
